import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { type IDynamicFieldsOptions } from '@pdqdev/pdq-shared/dist/interfaces/IDynamicFields';
import {
  UploadIconDialogComponent,
  UploadIconDialogResult,
} from '@app/sharedModule/components/upload-icon-dialog/upload-icon-dialog.component';
import {
  BADGE_CORNER_RADIUS_OPTIONS,
  BADGE_ICON_OPTIONS,
  BADGE_TYPE_OPTIONS,
  BadgeFormGroup,
  UrgencyBoostExtension,
  WINDOW_UNIT_OPTIONS,
  applyBadgeTypeFields,
} from '../../../../model';

const AUTOCOMPLETE_OPTIONS_BY_TYPE: Record<UrgencyBoostExtension.BadgeType, IDynamicFieldsOptions[]> = {
  [UrgencyBoostExtension.BadgeType.LOW_STOCK]: [
    {
      name: '{count}',
      searchTerm: 'count',
      description: 'Remaining stock',
      displayName: 'Count',
      category: 'Stock',
    },
  ],
  [UrgencyBoostExtension.BadgeType.RECENT_PURCHASES]: [
    {
      name: '{count}',
      searchTerm: 'count',
      description: 'Items bought in the window',
      displayName: 'Count',
      category: 'Purchases',
    },
    {
      name: '{hours}',
      searchTerm: 'hours',
      description: 'Window length in hours',
      displayName: 'Hours',
      category: 'Window',
    },
    {
      name: '{days}',
      searchTerm: 'days',
      description: 'Window length in days',
      displayName: 'Days',
      category: 'Window',
    },
  ],
  [UrgencyBoostExtension.BadgeType.CUSTOM_MESSAGE]: [],
};

@Component({
  selector: 'badge-editor',
  templateUrl: './badge-editor.component.html',
  styleUrls: ['./badge-editor.component.scss'],
  standalone: false,
})
export class BadgeEditorComponent implements OnInit, OnDestroy {
  @Input() badgeForm: FormGroup<BadgeFormGroup>;
  @Output() removeRequested = new EventEmitter<void>();
  @Output() duplicateRequested = new EventEmitter<void>();

  badgeTypeOptions = BADGE_TYPE_OPTIONS;
  cornerRadiusOptions = BADGE_CORNER_RADIUS_OPTIONS;
  windowUnitOptions = WINDOW_UNIT_OPTIONS;

  readonly presetIconKeys = BADGE_ICON_OPTIONS.map((o) => o.key);
  readonly presetIconLabels = BADGE_ICON_OPTIONS.map((o) => o.label);
  customIconSrc = '';

  private currentObjectUrl: string | null = null;

  constructor(private dialog: MatDialog) {}

  get backgroundColorControl(): FormControl<string> {
    return this.badgeForm.controls.backgroundColor;
  }

  get textColorControl(): FormControl<string> {
    return this.badgeForm.controls.textColor;
  }

  get customIconUrlControl(): FormControl<string | File | null> {
    return this.badgeForm.controls.customIconUrl;
  }

  get iconValue(): string {
    return this.badgeForm.controls.icon.value ?? '';
  }

  BadgeType = UrgencyBoostExtension.BadgeType;
  RICH_TEXT_TEMPLATE_MAX_LENGTH = UrgencyBoostExtension.RICH_TEXT_TEMPLATE_MAX_LENGTH;
  // Hide alignment, list, and heading toolbar groups — badges are single-line
  // pills, so only inline text formatting (bold/italic/underline/strike) and
  // emoji are meaningful here.
  readonly hiddenRichTextFormats = ['align', 'list', 'header'] as const;
  expanded = true;
  editingName = false;

  toggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  startEditingName(): void {
    this.editingName = true;
  }

  stopEditingName(): void {
    this.editingName = false;
  }

  get customMessageDisplayName(): string {
    return this.badgeForm.controls.name?.value || 'Custom message';
  }

  onIconUrlChange(value: string | File): void {
    // The icon control persists a string (preset key, custom icon URL, or '').
    // The picker only emits the string custom source we feed it, never a File.
    const next = typeof value === 'string' ? value : this.customIconSrc;
    if (this.badgeForm.controls.icon.value === next) return;
    this.badgeForm.controls.icon.setValue(next);
    this.badgeForm.controls.icon.markAsDirty();
  }

  onRemoveCustomIcon(): void {
    const wasSelected = !!this.customIconSrc && this.iconValue === this.customIconSrc;
    this.customIconUrlControl.setValue(null);
    this.customIconUrlControl.markAsDirty();
    if (wasSelected) {
      this.badgeForm.controls.icon.setValue('');
      this.badgeForm.controls.icon.markAsDirty();
    }
  }

  onUploadCustomIcon(): void {
    this.dialog
      .open<UploadIconDialogComponent, void, UploadIconDialogResult | null>(UploadIconDialogComponent, {
        width: '576px',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) return;
        // setValue triggers the customIconUrl subscription synchronously, which
        // updates customIconSrc — read it afterwards to select the new icon.
        this.customIconUrlControl.setValue(result.file);
        this.customIconUrlControl.markAsDirty();
        this.badgeForm.controls.icon.setValue(this.customIconSrc);
        this.badgeForm.controls.icon.markAsDirty();
      });
  }

  clearBackgroundColor(): void {
    this.backgroundColorControl.setValue('#0000');
    this.backgroundColorControl.markAsDirty();
  }

  windowUnitDropdownOptions = WINDOW_UNIT_OPTIONS.map((o) => ({ label: o.label, value: o.value }));

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.badgeForm.controls.type.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((type: UrgencyBoostExtension.BadgeType) => applyBadgeTypeFields(this.badgeForm, type));

    this.refreshCustomIconSrc(this.customIconUrlControl.value);
    this.customIconUrlControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.refreshCustomIconSrc(value));
  }

  ngOnDestroy(): void {
    this.revokeObjectUrl();
  }

  private refreshCustomIconSrc(value: string | File | null): void {
    this.revokeObjectUrl();
    if (value instanceof File) {
      this.currentObjectUrl = URL.createObjectURL(value);
      this.customIconSrc = this.currentObjectUrl;
    } else if (typeof value === 'string' && value) {
      this.customIconSrc = value;
    } else {
      this.customIconSrc = '';
    }
  }

  private revokeObjectUrl(): void {
    if (this.currentObjectUrl) {
      URL.revokeObjectURL(this.currentObjectUrl);
      this.currentObjectUrl = null;
    }
  }

  get currentType(): UrgencyBoostExtension.BadgeType {
    return this.badgeForm.controls.type.value;
  }

  badgeTypeLabel(type: UrgencyBoostExtension.BadgeType): string {
    return this.badgeTypeOptions.find((o) => o.value === type)?.label ?? '';
  }

  get richTextConfig() {
    return {
      items: AUTOCOMPLETE_OPTIONS_BY_TYPE[this.currentType] ?? [],
      triggerChar: '{',
      filterByKey: 'searchTerm',
      removeTriggerCharOnSelect: true,
      descriptionKey: 'description',
      variant: 'categorizedOptions',
    };
  }

  get currentPlaceholder(): string {
    switch (this.currentType) {
      case UrgencyBoostExtension.BadgeType.LOW_STOCK:
        return 'Only {count} left - selling fast!';
      case UrgencyBoostExtension.BadgeType.RECENT_PURCHASES:
        return '{count} bought this in the last {hours} h';
      case UrgencyBoostExtension.BadgeType.CUSTOM_MESSAGE:
        return 'Limited edition';
      default:
        return '';
    }
  }
}
